// Worker クライアント (シングルトン)。Worker が使えない環境ではメインスレッドで同じ関数を実行する。
import { WorkerClient } from './rpc'
import { encode as encodePng } from 'fast-png'
import { featherMask, expandMask, borderMask } from '@/selection/modify'
import { magicWand, type WandOptions } from '@/selection/floodFill'
import type { MaskData } from '@/selection/mask'
import type { Rect } from '@/types/geometry'
import { runCpuFilter } from '@/filters/cpu'
import { spotHeal, healBlend, contentAwareFill } from '@/retouch/heal'
import type { FilterParams } from '@/filters/registry'

const hasWorker = typeof Worker !== 'undefined'

export const ioClient = new WorkerClient(() => new Worker(new URL('./ioWorker.ts', import.meta.url), { type: 'module' }))
export const selectionClient = new WorkerClient(() => new Worker(new URL('./selectionWorker.ts', import.meta.url), { type: 'module' }))
export const retouchClient = new WorkerClient(() => new Worker(new URL('./retouchWorker.ts', import.meta.url), { type: 'module' }))
export const filterClient = new WorkerClient(() => new Worker(new URL('./filterWorker.ts', import.meta.url), { type: 'module' }))

/** PNG エンコード (Worker、フォールバックあり)。data は転送されるので呼び出し後は使わない */
export async function encodePngAsync(width: number, height: number, channels: 1 | 4, data: Uint8ClampedArray): Promise<Uint8Array> {
  if (hasWorker && ioClient.available) {
    try {
      return await ioClient.call<Uint8Array>('encodePng', { width, height, channels, data }, [data.buffer])
    } catch {
      /* フォールバック */
    }
  }
  return encodePng({ width, height, data: new Uint8Array(data.buffer, data.byteOffset, data.byteLength), channels, depth: 8 })
}

export async function modifyMaskAsync(mask: MaskData, kind: 'feather' | 'expand' | 'contract' | 'border', amount: number): Promise<MaskData | null> {
  if (hasWorker && selectionClient.available) {
    try {
      const copy: MaskData = { rect: { ...mask.rect }, data: new Uint8ClampedArray(mask.data) }
      return await selectionClient.call<MaskData | null>('modify', { mask: copy, kind, amount }, [copy.data.buffer])
    } catch {
      /* フォールバック */
    }
  }
  return kind === 'feather' ? featherMask(mask, amount) : kind === 'expand' ? expandMask(mask, amount) : kind === 'contract' ? expandMask(mask, -amount) : borderMask(mask, amount)
}

export async function magicWandAsync(rgba: Uint8ClampedArray, rect: Rect, seed: { x: number; y: number }, opts: WandOptions): Promise<MaskData | null> {
  if (hasWorker && selectionClient.available) {
    try {
      const copy = new Uint8ClampedArray(rgba)
      return await selectionClient.call<MaskData | null>('wand', { rgba: copy, rect, seed, opts }, [copy.buffer])
    } catch {
      /* フォールバック */
    }
  }
  return magicWand(rgba, rect, seed, opts)
}

/** CPU フィルター (Worker、フォールバックあり)。rgba は転送されるので呼び出し後は使わない */
export async function runCpuFilterAsync(id: string, rgba: Uint8ClampedArray, w: number, h: number, params: FilterParams): Promise<Uint8ClampedArray> {
  if (hasWorker && filterClient.available) {
    try {
      return await filterClient.call<Uint8ClampedArray>('run', { id, rgba, w, h, params }, [rgba.buffer])
    } catch {
      /* フォールバック (転送済みなら空になっているので読み直しは呼び出し側) */
      if (rgba.byteLength === 0) throw new Error('Worker が失敗した')
    }
  }
  return runCpuFilter(id, rgba, w, h, params)
}

/** レタッチ (Worker、フォールバックあり)。配列は複製して渡す */
export async function retouchAsync(kind: 'spotHeal' | 'fill', img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray): Promise<Uint8ClampedArray>
export async function retouchAsync(kind: 'heal', img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray, cloned: Uint8ClampedArray): Promise<Uint8ClampedArray>
export async function retouchAsync(kind: 'spotHeal' | 'fill' | 'heal', img: Uint8ClampedArray, w: number, h: number, mask: Uint8ClampedArray, cloned?: Uint8ClampedArray): Promise<Uint8ClampedArray> {
  if (hasWorker && retouchClient.available) {
    try {
      const a = new Uint8ClampedArray(img), m = new Uint8ClampedArray(mask)
      if (kind === 'heal') {
        const c = new Uint8ClampedArray(cloned!)
        return await retouchClient.call<Uint8ClampedArray>('heal', { original: a, cloned: c, w, h, mask: m }, [a.buffer, c.buffer, m.buffer])
      }
      return await retouchClient.call<Uint8ClampedArray>(kind, { img: a, w, h, mask: m }, [a.buffer, m.buffer])
    } catch {
      /* フォールバック */
    }
  }
  return kind === 'heal' ? healBlend(img, cloned!, mask, w, h) : kind === 'spotHeal' ? spotHeal(img, w, h, mask) : contentAwareFill(img, w, h, mask)
}
